"use client";

import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function DirectAnswerBlock() {
  const hero = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const headline = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const narrative = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const quote = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.3
  });
  const midImage = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const timeline = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.25
  });
  const signature = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.3
  });
  const milestones = [{
    year: "2015",
    event: "Precision Lab founded"
  }, {
    year: "2017",
    event: "Laser tech integration"
  }, {
    year: "2019",
    event: "Industrial marking expansion"
  }, {
    year: "2021",
    event: "Automated DPM systems"
  }, {
    year: "2024",
    event: "Advanced submittal scaling"
  }];
  return <section data-section-id="2788" id="direct-answer-block" className="bg-background text-foreground">
      <div ref={hero.ref} className={`relative w-full h-[600px] overflow-hidden transition-all duration-1000 ease-out ${hero.isVisible ? "opacity-100" : "opacity-0"}`}>
        <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/industrial_engraving_facility.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/industrial_engraving_facility.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/industrial_engraving_facility.png-wvc-sizes" alt="Precision industrial engraving facility" loading="lazy" decoding="async" className="absolute inset-0 w-full object-cover grayscale !h-[600px]" />
        <div className="absolute inset-0 bg-background/40" />

        <div className="absolute bottom-6 right-6 md:bottom-10 md:right-12 font-mono text-[10px] md:text-xs tracking-widest text-foreground/60">
          ENGINEERING — PRECISION
        </div>

        <div className="absolute bottom-6 left-6 md:bottom-10 md:left-12 font-mono text-[10px] md:text-xs tracking-widest text-foreground/60">
          ARCHIVE / PRODUCTION 001
        </div>
      </div>

      <div className="light bg-background text-foreground">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-32">
          <div ref={headline.ref} className={`grid grid-cols-1 lg:grid-cols-12 gap-8 transition-all duration-1000 ${headline.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
            <div className="lg:col-span-2 lg:col-start-3">
              <div className="font-mono text-xs md:text-sm tracking-[0.25em] uppercase" style={{
              color: "var(--primary)"
            }}>
                01 / HISTORY
              </div>
            </div>

            <div className="lg:col-span-8 lg:col-start-3 mt-2">
              <h2 className="font-default font-light text-[2.75rem] sm:text-6xl md:text-7xl lg:text-8xl leading-[0.95] tracking-tight text-foreground">
                DEFINING INDUSTRIAL PRECISION<span style={{
                color: "var(--primary)"
              }}>.</span>
              </h2>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mt-16 md:mt-24">
            <article ref={narrative.ref} className={`lg:col-span-8 lg:col-start-3 transition-all duration-1000 delay-150 ${narrative.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
              <p className="font-default text-lg md:text-[1.125rem] leading-[1.7] text-foreground max-w-[65ch]"><span className="float-left font-default font-light text-[5.5rem] md:text-[7rem] leading-[0.8] mr-3 mt-2 text-foreground" data-ast-id="f:/src/pages/Industries/sections/DirectAnswerBlock.tsx|sig:2cw8en" data-ast-start="3979" data-ast-end="4145">W</span>e started Evermark with a singular focus: to provide the most durable, precise identification solutions for the industrial sector. At the time, the market was flooded with low-quality labels that failed under harsh conditions. We saw an opportunity to apply aerospace-grade standards to everyday industrial marking.</p>

              <p className="font-default text-lg md:text-[1.125rem] leading-[1.7] text-foreground max-w-[65ch] mt-8">Our facility was built to bridge the gap between raw manufacturing and high-fidelity identification. We don't just engrave; we engineer solutions that withstand extreme environments. From phenol labels to complex direct part marking, our process is rooted in rigorous quality control and advanced laser technology.</p>

              

              <p className="font-default text-lg md:text-[1.125rem] leading-[1.7] text-foreground max-w-[65ch]">
                Today, we serve a diverse range of industries, providing everything from custom phenolic tags to high-speed DPM services. Our team of specialists ensures that every submittal package meets the exact specifications of our clients, maintaining the integrity of their systems through superior marking and identification.
              </p>
            </article>

            <div ref={midImage.ref} className={`lg:col-span-12 mt-12 md:mt-20 transition-all duration-1000 ${midImage.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
              <figure>
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/laser_engraving_process.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/laser_engraving_process.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/laser_engraving_process.png-wvc-sizes" alt="Advanced laser engraving process in action" loading="lazy" decoding="async" className="w-full h-[40vh] md:h-[65vh] object-cover grayscale" />
                <figcaption className="font-mono text-[10px] md:text-xs tracking-widest uppercase text-foreground/60 mt-4 flex items-center gap-3">
                  <span className="inline-block h-px w-6 bg-foreground/40" />
                  <span>FIG. 02 — PRECISION LASER CALIBRATION, 2026</span>
                </figcaption>
              </figure>
            </div>

            <article className="lg:col-span-8 lg:col-start-3 mt-16 md:mt-24">
              <p className="font-default text-lg md:text-[1.125rem] leading-[1.7] text-foreground max-w-[65ch]">
                We continue to evolve, investing in the latest automation and material science to ensure our clients stay ahead of regulatory requirements and operational demands.
              </p>
            </article>
          </div>

          

          <div ref={signature.ref} className={`mt-24 md:mt-32 grid grid-cols-1 lg:grid-cols-12 gap-8 transition-all duration-1000 ${signature.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
            <div className="lg:col-span-8 lg:col-start-3">
              <svg viewBox="0 0 320 80" className="w-48 md:w-64 h-auto mb-4" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                <path d="M5 55 C 25 25, 45 70, 65 40 S 110 15, 130 50 C 145 70, 160 35, 180 45 S 220 70, 245 30 C 260 10, 285 60, 315 35" stroke="currentColor" strokeWidth="2" strokeLinecap="round" fill="none" />
                <path d="M55 65 L 270 65" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeDasharray="2 4" opacity="0.4" />
              </svg>
              <div className="font-mono text-xs md:text-sm tracking-[0.25em] uppercase text-foreground flex items-center gap-3">
                <span className="inline-block h-px w-6" style={{
                background: "var(--primary)"
              }} />
                <span>ENGINEERING TEAM </span>
              </div>
            </div>
          </div>
        </div>

        <div className="relative h-px w-full bg-foreground/20" />
      </div>

      <div className="h-16 md:h-24 bg-background" />
    </section>;
}